import { apiDelete, apiGet, apiPost, apiPostForm, apiPut } from "./http";
import type { ImageCategory, LibraryImage } from "../pages/platform/utils/imageLibraryStore";

type LibraryImageRow = {
  id: string;
  url: string;
  name: string;
  category: string;
  uploadedAt: string;
  isLocal?: boolean;
};

function rowToLibraryImage(r: LibraryImageRow): LibraryImage | null {
  if (!r?.id?.trim() || !r?.url?.trim()) return null;
  return {
    id: r.id.trim(),
    url: r.url.trim(),
    name: (r.name ?? "").trim() || r.id,
    category: (r.category as ImageCategory) ?? "general",
    uploadedAt: r.uploadedAt ?? "",
    isLocal: r.isLocal,
  };
}

export async function fetchPlatformLibraryImages(): Promise<LibraryImage[]> {
  const data = await apiGet<unknown>("/api/platform/library-images");
  if (!Array.isArray(data)) {
    console.error("[fetchPlatformLibraryImages] API 应返回数组，实际为:", data);
    return [];
  }
  return data
    .map((row) => rowToLibraryImage(row as LibraryImageRow))
    .filter((x): x is LibraryImage => x != null);
}

/** 租户管理端：用租户 JWT 读取同一套运营平台图库列表（勿调用 `/api/platform/*`，否则无平台 token 会失败）。 */
export async function fetchTenantPlatformLibraryImages(tenantLegacyId: string): Promise<LibraryImage[]> {
  const tid = tenantLegacyId.trim();
  if (!tid) return [];
  const data = await apiGet<unknown>(`/api/tenants/${encodeURIComponent(tid)}/platform-library-images`);
  if (!Array.isArray(data)) {
    console.error("[fetchTenantPlatformLibraryImages] API 应返回数组，实际为:", data);
    return [];
  }
  return data
    .map((row) => rowToLibraryImage(row as LibraryImageRow))
    .filter((x): x is LibraryImage => x != null);
}

export async function createPlatformLibraryImage(body: {
  name: string;
  category: ImageCategory;
  url: string;
}): Promise<LibraryImage> {
  const row = await apiPost<LibraryImageRow>("/api/platform/library-images", body);
  const img = rowToLibraryImage(row);
  if (!img) throw new Error("创建成功但返回数据无效");
  return img;
}

export async function updatePlatformLibraryImage(
  legacyId: string,
  body: Partial<{ name: string; category: ImageCategory; url: string }>,
): Promise<LibraryImage> {
  const row = await apiPut<LibraryImageRow>(`/api/platform/library-images/${encodeURIComponent(legacyId)}`, body);
  const img = rowToLibraryImage(row);
  if (!img) throw new Error("更新成功但返回数据无效");
  return img;
}

export async function deletePlatformLibraryImage(legacyId: string): Promise<void> {
  await apiDelete(`/api/platform/library-images/${encodeURIComponent(legacyId)}`);
}

/** 平台端：按数据库中当前图片 Url 由服务端下载并转存 OSS，返回更新后的条目（rehosted=false 表示已在 OSS 未改写）。 */
export async function rehostPlatformLibraryImageFromUrl(
  legacyId: string,
): Promise<{ image: LibraryImage; rehosted: boolean }> {
  const row = await apiPost<LibraryImageRow & { rehosted: boolean }>(
    `/api/platform/library-images/${encodeURIComponent(legacyId)}/rehost-to-oss`,
    {},
  );
  const img = rowToLibraryImage(row);
  if (!img) throw new Error("转存返回数据无效");
  return { image: img, rehosted: row.rehosted };
}

/** 平台端：图片二进制上传到 OSS，返回公网 URL */
export async function uploadPlatformImageToOss(file: File): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await apiPostForm<{ url: string }>("/api/platform/storage/upload-image", fd);
  return res.url;
}
